// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCell, DataTableRow, LazyImage, TextField, Tooltip, Typography } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import { tableProps } from "@store/Themes/common/props/table";
import { usePendingQuantity } from "@store/Framework/Store/Store/Data/BackOrders/BackOrdersHooks";
import setDeleteModalIsOpen from "@store/Framework/Store/Store/Data/BackOrders/Handlers/SetDeleteModalIsOpen";
import setPendingQuantity from "@store/Framework/Store/Store/Data/BackOrders/Handlers/SetPendingQuantity";
import setSelectedBackOrderItem from "@store/Framework/Store/Store/Data/BackOrders/Handlers/SetSelectedBackOrderItem";
import setUpdateModalIsOpen from "@store/Framework/Store/Store/Data/BackOrders/Handlers/SetUpdateModalIsOpen";
import { BackOrderItem, BackOrderModel } from "@store/Framework/Types/ApiModels";
import { productLineProps } from "@store/Themes/common/props/productLine";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type BackOrdersRowProps = {
    backOrderItem: BackOrderModel | null;
    showActions?: boolean;
    showEditAction?: boolean;
    editQty?: boolean;
    url?: string;
    buttonText?: string;
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        language: state.context.session.language,
    };
};

type Props = BackOrdersRowProps & ReturnType<typeof mapStateToProps>;

export interface BackOrdersRowStyles {
    image?: LazyImageProps;
    descriptionWrapper?: InjectableCss;
    linkStyles?: LinkPresentationProps;
    quantityTextField?: TextFieldPresentationProps;
    actionsCell?: DataTableCellProps;
    editNotesLink?: LinkPresentationProps;
    actionDeleteLink?: LinkPresentationProps;
    contactSupportButton?: ButtonPresentationProps;
}

export const backOrdersRowStyles: BackOrdersRowStyles = {
    image: {
        css: css`
            ${x => x.theme.lazyImage.defaultProps?.css}
            display: flex;

            img {
                width: 90px;
            }
        `,
    },
    descriptionWrapper: {
        css: css`
            max-width: ${pxToRem(300)};
        `,
    },
    linkStyles: {
        css: css`
            white-space: normal;
        `,
    },
    quantityTextField: productLineProps.quantity?.editableQuantityTextField,
    actionsCell: tableProps.narrowCell,
    editNotesLink: productLineProps.editNotesLink,
    actionDeleteLink: productLineProps.removeLink,
    contactSupportButton: {
        sizeVariant: "small",
    },
};

const styles = backOrdersRowStyles;

const BackOrdersRow: FC<Props> = ({
    backOrderItem,
    showActions,
    showEditAction,
    editQty,
    language,
    url,
    buttonText,
}) => {
    const dispatch = useAppDispatch();
    const pendingQuantity = usePendingQuantity();

    if (!backOrderItem) {
        return null;
    }

    const {
        id,
        product,
        backOrderQuantity,
        unitNetPrice,
        originalOrderDate,
        productNextAvailabilityDate,
        originalOrderNumber,
    } = backOrderItem;

    // Normalize raw API model once so render + actions share the same shape.
    const backOrderRowItem: BackOrderItem = {
        id,
        title: product?.productTitle || "",
        sku: (product?.productDetail?.name ?? product?.productDetail?.sku ?? "") as string,
        quantity: backOrderQuantity,
        price: unitNetPrice,
        originalOrderDate: originalOrderDate
            ? getLocalizedDateTime({
                  dateTime: new Date(originalOrderDate),
                  language,
              })
            : "",
        nextAvailability: productNextAvailabilityDate
            ? getLocalizedDateTime({
                  dateTime: new Date(productNextAvailabilityDate),
                  language,
              })
            : null,
        originalOrderNumber,
        imagePath: product?.properties?.smallImagePath || "",
        canonicalUrl: product?.canonicalUrl || "",
        hasProduct: !!product,
    };

    const updateQuantity = (quantity: string) => {
        dispatch(setPendingQuantity({ pendingQuantity: Number.parseInt(quantity, 10) || 0 }));
    };

    const openDeleteModal = () => {
        dispatch(setDeleteModalIsOpen({ isOpen: true }));
        dispatch(setSelectedBackOrderItem({ backOrderItem }));
    };

    const openUpdateQuantityModal = () => {
        dispatch(setUpdateModalIsOpen({ isOpen: true }));
        dispatch(setSelectedBackOrderItem({ backOrderItem }));
    };

    return (
        <DataTableRow>
            <DataTableCell>
                {backOrderRowItem.hasProduct && backOrderRowItem.imagePath && (
                    <LazyImage {...styles.image} src={backOrderRowItem.imagePath} altText={backOrderRowItem.title} />
                )}
            </DataTableCell>
            <DataTableCell>
                <StyledWrapper {...styles.descriptionWrapper}>
                    {backOrderRowItem.hasProduct && backOrderRowItem.canonicalUrl ? (
                        <Link href={backOrderRowItem.canonicalUrl} {...styles.linkStyles}>
                            {backOrderRowItem.title}
                        </Link>
                    ) : (
                        <Typography>{backOrderRowItem.title}</Typography>
                    )}
                    <br />
                    <Typography>{backOrderRowItem.sku}</Typography>
                    <br />
                    <Typography>
                        {translate("Price")}: {backOrderRowItem.price}
                    </Typography>
                    <br />
                    <Typography>
                        {translate("Order Number")}: {backOrderRowItem.originalOrderNumber}
                    </Typography>
                </StyledWrapper>
            </DataTableCell>
            <DataTableCell>
                <Typography>{backOrderRowItem.originalOrderDate}</Typography>
            </DataTableCell>
            <DataTableCell>
                <Typography>{backOrderRowItem.nextAvailability || translate("No expected date exists")}</Typography>
            </DataTableCell>
            <DataTableCell>
                {editQty ? (
                    <TextField
                        {...styles.quantityTextField}
                        label={translate("New Qty")}
                        type="number"
                        min={0}
                        max={999999}
                        value={pendingQuantity}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateQuantity(e.target.value)}
                        data-test-selector="backOrdersList_quantityInput"
                    />
                ) : (
                    <Typography>{backOrderRowItem.quantity}</Typography>
                )}
            </DataTableCell>
            <DataTableCell {...styles.actionsCell}>
                {showActions && !backOrderItem.isUnderReview && (
                    <StyledWrapper>
                        {showEditAction && (
                            <Link
                                {...styles.editNotesLink}
                                data-test-selector={`backOrderLineEditButton`}
                                onClick={() => openUpdateQuantityModal()}
                            >
                                {translate("Edit")}
                            </Link>
                        )}
                        <Link
                            {...styles.actionDeleteLink}
                            data-test-selector={`backOrderLineDeleteButton`}
                            onClick={() => openDeleteModal()}
                        >
                            {translate("Delete")}
                        </Link>
                    </StyledWrapper>
                )}
                {showActions && backOrderItem.isUnderReview && (
                    <StyledWrapper>
                        <Typography>{translate("Pending review")}</Typography>
                        <Tooltip text={siteMessage("Backorder_PendingReview").toString()} />
                    </StyledWrapper>
                )}
                {/* No destination means there is nothing to link to: the modal reuses this row without one. */}
                {!showActions && url && (
                    <StyledWrapper>
                        <Button
                            {...styles.contactSupportButton}
                            data-test-selector="backOrders_contactSupport"
                            href={url}
                        >
                            {buttonText}
                        </Button>
                    </StyledWrapper>
                )}
            </DataTableCell>
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(BackOrdersRow);
